import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { List } from '@usepepper/sprout-kit-ui';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { ScrollView, StyleProp, View, ViewStyle } from 'react-native';
import {
  Timeslot,
  TimeslotOption,
} from '@domain/fulfillment-settings/types/types';
import { Typography } from '@usepepper/ui/atoms/typography';

interface Props {
  onSelect: (timeslot: Timeslot) => void;
  style?: StyleProp<ViewStyle>;
  timeslotOptions: TimeslotOption[];
}

export const FulfillmentActionSheetTimeslotView: React.FC<Props> = ({
  timeslotOptions,
  onSelect,
  style,
}) => {
  const [styles, theme] = useStyles();
  const { availableTimeslots, disabledTimeslots } = useMemo(() => {
    const available: TimeslotOption[] = [];
    const disabled: TimeslotOption[] = [];
    for (const timeslot of timeslotOptions) {
      if (timeslot.metadata?.disabled) {
        disabled.push(timeslot);
      } else {
        available.push(timeslot);
      }
    }
    return { availableTimeslots: available, disabledTimeslots: disabled };
  }, [timeslotOptions]);

  const renderTimeslot = (option: TimeslotOption, index: number) => (
    <React.Fragment key={String(option.value)}>
      {index > 0 ? <List.Separator marginHorizontal={SCREEN_PADDING} /> : null}
      <List.Item
        disabled={Boolean(option.metadata?.disabled)}
        onPress={() => {
          if (!option.metadata?.disabled) {
            onSelect(option.metadata!.timeslot);
          }
        }}
        paddingHorizontal={SCREEN_PADDING}
        paddingVertical={theme.sk.spacing.spaceMedium}
        pressable
      >
        <List.Item.Content>
          <List.Item.Title style={[TYPOGRAPHY.listTitle, styles.title]}>
            {option.title}
          </List.Item.Title>
        </List.Item.Content>
      </List.Item>
    </React.Fragment>
  );

  return (
    <ScrollView
      nestedScrollEnabled
      scrollIndicatorInsets={{ right: 1 }}
      style={style}
    >
      <List>
        {availableTimeslots.map(renderTimeslot)}
        {disabledTimeslots.length > 0 ? (
          <>
            <View style={styles.sectionHeader}>
              <Typography
                color={theme.sk.color.text.secondary}
                variant="heading-sm"
              >
                {i18n.t('past')}
              </Typography>
            </View>
            {disabledTimeslots.map(renderTimeslot)}
          </>
        ) : null}
      </List>
    </ScrollView>
  );
};

const useStyles = createStyles((theme) => ({
  sectionHeader: {
    paddingHorizontal: SCREEN_PADDING,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  title: {
    textTransform: 'uppercase',
  },
}));
